import React, { useEffect, useMemo, useState } from "react";
import {
  getRoles,
  getRole,
  createRole,
  updateRole,
  deleteRole,
  setRolePermissions,
  getPermissionCatalog,
  getPermissionPresets,
  syncPermissionCatalog,
} from "../services/api";
import { usePermissions } from "../context/PermissionContext";
import AlertModal from "../components/AlertModal";
import ConfirmModal from "../components/ConfirmModal";
import "./RoleManagement.css";

interface RoleRow {
  roleId: number;
  name: string;
  description?: string;
  isSystem: boolean;
  displayOrder: number;
  permissionCount: number;
  userCount: number;
}
interface PresetItem { code: string; name: string; description: string; autoGrant: boolean; permissionIds: number[] }
interface CatalogItem {
  permissionId: number;
  code: string;
  screenCode: string;
  screenName: string;
  tabCode: string | null;
  tabName: string | null;
  action: string;
  category: string;
  displayOrder: number;
}

const ACTION_LABELS: Record<string, string> = {
  view: "Görüntüle",
  create: "Ekle",
  update: "Güncelle",
  delete: "Sil",
  export: "Dışa Aktar",
  transfer: "Transfer",
  send: "Fatura Oluştur",
  manage: "İptal / İtiraz",
  approve: "Onayla / Reddet",
  post: "Cariye İşle",
};
const ACTION_ORDER = ["view", "create", "update", "delete", "export", "transfer", "send", "manage", "approve", "post"];

const RoleManagement: React.FC = () => {
  const { refresh: refreshPerms } = usePermissions();
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [presets, setPresets] = useState<PresetItem[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedPerms, setSelectedPerms] = useState<Set<number>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [showCodes, setShowCodes] = useState(false);

  const [roleModal, setRoleModal] = useState<{
    open: boolean;
    id: number | null;
    name: string;
    description: string;
    isSystem: boolean;
  }>({ open: false, id: null, name: "", description: "", isSystem: false });

  const [alert, setAlert] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });
  const [confirm, setConfirm] = useState<{
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
  }>({ isOpen: false, message: "", onConfirm: () => {} });

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [rolesRes, catRes, preRes] = await Promise.all([getRoles(), getPermissionCatalog(), getPermissionPresets().catch(() => ({ data: [] }))]);
      const rolesData: RoleRow[] = rolesRes.data || [];
      setRoles(rolesData);
      setCatalog(catRes.data || []);
      setPresets((preRes.data || []).filter((p: PresetItem) => p.permissionIds.length > 0));
      if (rolesData.length > 0) await selectRole(selectedId ?? rolesData[0].roleId);
    } catch {
      setAlert({ isOpen: true, message: "Roller/izin kataloğu yüklenemedi.", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const selectRole = async (id: number) => {
    try {
      const res = await getRole(id);
      setSelectedId(id);
      setSelectedPerms(new Set<number>(res.data?.permissionIds || []));
      setDirty(false);
    } catch {
      setAlert({ isOpen: true, message: "Rol izinleri yüklenemedi.", type: "error" });
    }
  };

  const selectedRole = roles.find((r) => r.roleId === selectedId) || null;

  // Katalog → kategori ▸ ekran ▸ item[]
  const grouped = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    const filtered = q
      ? catalog.filter(
          (c) =>
            c.screenName.toLocaleLowerCase("tr").includes(q) ||
            (c.tabName || "").toLocaleLowerCase("tr").includes(q) ||
            c.category.toLocaleLowerCase("tr").includes(q) ||
            c.code.includes(q),
        )
      : catalog;
    const byCat = new Map<string, Map<string, CatalogItem[]>>();
    for (const it of [...filtered].sort((a, b) => a.displayOrder - b.displayOrder)) {
      if (!byCat.has(it.category)) byCat.set(it.category, new Map());
      const screens = byCat.get(it.category)!;
      if (!screens.has(it.screenCode)) screens.set(it.screenCode, []);
      screens.get(it.screenCode)!.push(it);
    }
    return byCat;
  }, [catalog, search]);

  const setMany = (ids: number[], value: boolean) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (value ? next.add(id) : next.delete(id)));
      return next;
    });
    setDirty(true);
  };
  const toggle = (id: number) => {
    setSelectedPerms((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setDirty(true);
  };

  const savePermissions = async () => {
    if (selectedId == null) return;
    try {
      setSaving(true);
      await setRolePermissions(selectedId, Array.from(selectedPerms));
      setAlert({ isOpen: true, message: "Rol yetkileri kaydedildi.", type: "success" });
      setDirty(false);
      await refreshPerms();
      setRoles((await getRoles()).data || []);
    } catch {
      setAlert({ isOpen: true, message: "Yetkiler kaydedilemedi.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    try {
      setSaving(true);
      const res = await syncPermissionCatalog();
      setAlert({
        isOpen: true,
        message: `Katalog senkronlandı. ${res.data?.added ?? 0} yeni yetki eklendi.${res.data?.presetsSeeded ? ` İK self-servis paketi ${res.data.presetsSeeded} role verildi (${(res.data.seededRoles || []).join(", ")}).` : ""}`,
        type: "success",
      });
      await loadAll();
    } catch {
      setAlert({ isOpen: true, message: "Katalog senkronlanamadı.", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () =>
    setRoleModal({ open: true, id: null, name: "", description: "", isSystem: false });
  const openEdit = (r: RoleRow) =>
    setRoleModal({ open: true, id: r.roleId, name: r.name, description: r.description || "", isSystem: r.isSystem });

  const saveRole = async () => {
    if (!roleModal.name.trim() && !roleModal.isSystem) {
      setAlert({ isOpen: true, message: "Rol adı zorunludur.", type: "warning" });
      return;
    }
    try {
      setSaving(true);
      if (roleModal.id == null) {
        const res = await createRole({ name: roleModal.name.trim(), description: roleModal.description });
        setRoleModal({ ...roleModal, open: false });
        setRoles((await getRoles()).data || []);
        if (res.data?.roleId) await selectRole(res.data.roleId);
      } else {
        await updateRole(roleModal.id, { name: roleModal.name.trim(), description: roleModal.description });
        setRoleModal({ ...roleModal, open: false });
        setRoles((await getRoles()).data || []);
      }
    } catch (e: any) {
      setAlert({ isOpen: true, message: "Rol kaydedilemedi: " + (e.response?.data || e.message), type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (r: RoleRow) =>
    setConfirm({
      isOpen: true,
      message: `"${r.name}" rolünü silmek istediğinize emin misiniz?`,
      onConfirm: async () => {
        try {
          await deleteRole(r.roleId);
          setConfirm({ isOpen: false, message: "", onConfirm: () => {} });
          if (selectedId === r.roleId) setSelectedId(null);
          setRoles((await getRoles()).data || []);
        } catch (e: any) {
          setConfirm({ isOpen: false, message: "", onConfirm: () => {} });
          setAlert({ isOpen: true, message: "Rol silinemedi: " + (e.response?.data || e.message), type: "error" });
        }
      },
    });

  const allCatalogIds = catalog.map((c) => c.permissionId);
  const allOn = allCatalogIds.length > 0 && allCatalogIds.every((id) => selectedPerms.has(id));

  if (loading) {
    return (
      <div className="rm">
        <div className="rm-loading"><div className="rm-spinner" /><span>Yükleniyor...</span></div>
      </div>
    );
  }

  return (
    <div className="rm">
      {/* Header */}
      <div className="rm-header">
        <div className="rm-breadcrumb">
          <span>Yönetim</span>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
          <span className="active">Rol Yönetimi</span>
        </div>
        <div className="rm-head-main">
          <div className="rm-head-left">
            <div className="rm-head-icon">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 7h-9M14 17H5M17 21a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM7 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />
              </svg>
            </div>
            <div>
              <h1>Rol Yönetimi</h1>
              <p>Rolleri ve ekran / sekme / işlem yetkilerini yönetin</p>
            </div>
          </div>
          <button className="btn btn-secondary" onClick={handleSync} disabled={saving} title="İzin kataloğunu güncelle">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg>
            Kataloğu Senkronla
          </button>
        </div>
      </div>

      {/* Rol seçici (üst şerit) */}
      <div className="rm-rolebar">
        {roles.map((r) => (
          <button
            key={r.roleId}
            className={`rm-role-chip ${selectedId === r.roleId ? "active" : ""}`}
            onClick={() => selectRole(r.roleId)}
          >
            <span className="rm-role-chip-name">{r.name}</span>
            <span className="rm-role-chip-count">{r.permissionCount}</span>
          </button>
        ))}
        <button className="rm-role-new" onClick={openCreate}>
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Yeni Rol
        </button>
      </div>

      {selectedRole ? (
        <div className="rm-panel">
          {/* Seçili rol başlığı */}
          <div className="rm-panel-head">
            <div className="rm-panel-role">
              <div className="rm-panel-role-title">
                <h2>{selectedRole.name}</h2>
                {selectedRole.isSystem && <span className="rm-badge-sys">Yerleşik</span>}
                <span className="rm-claim" title="Bu rolün yetki (claim) adı">
                  claim: <code>{selectedRole.name}</code>
                </span>
              </div>
              <p>
                {selectedRole.description || "Ekran ve işlem yetkilerini seçin"} ·{" "}
                {selectedPerms.size} yetki · {selectedRole.userCount} kullanıcı
              </p>
            </div>
            <div className="rm-panel-actions">
              <button className="btn btn-secondary btn-sm" onClick={() => openEdit(selectedRole)}>Düzenle</button>
              {!selectedRole.isSystem && (
                <button className="btn btn-sm btn-soft-danger" onClick={() => askDelete(selectedRole)}>Sil</button>
              )}
              <button className="btn btn-primary" onClick={savePermissions} disabled={!dirty || saving}>
                {saving ? "Kaydediliyor..." : "Yetkileri Kaydet"}
              </button>
            </div>
          </div>

          {/* Hazır paketler — tek tıkla demet ver / kaldır */}
          {presets.length > 0 && (
            <div className="rm-presets">
              <span className="rm-presets-label">Hazır paketler</span>
              {presets.map((p) => {
                const on = p.permissionIds.every((id) => selectedPerms.has(id));
                const some = !on && p.permissionIds.some((id) => selectedPerms.has(id));
                return (
                  <button key={p.code} type="button" className={`rm-preset ${on ? "on" : some ? "some" : ""}`} title={p.description} onClick={() => setMany(p.permissionIds, !on)}>
                    <span className="rm-preset-box">{on ? "✓" : some ? "–" : ""}</span>
                    {p.name}
                    {p.autoGrant && <em title="Katalog eşitlemesinde İK izni olmayan her role kendiliğinden verilir">otomatik</em>}
                  </button>
                );
              })}
            </div>
          )}

          {/* Araç çubuğu */}
          <div className="rm-toolbar">
            <div className="rm-search">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ekran / sekme / kod ara..." />
            </div>
            <label className="rm-toggle-codes">
              <input type="checkbox" checked={showCodes} onChange={(e) => setShowCodes(e.target.checked)} />
              Yetki kodlarını göster
            </label>
            <button className="rm-linkbtn" onClick={() => setMany(allCatalogIds, !allOn)}>
              {allOn ? "Tümünü temizle" : "Tümünü seç"}
            </button>
          </div>

          {catalog.length === 0 ? (
            <div className="rm-empty"><p>İzin kataloğu boş. Önce <b>Kataloğu Senkronla</b>'ya tıklayın.</p></div>
          ) : (
            <div className="rm-matrix">
              {Array.from(grouped.entries()).map(([category, screens]) => {
                // kategorideki mevcut aksiyon sütunları
                const present = new Set<string>();
                screens.forEach((items) => items.forEach((i) => present.add(i.action)));
                const cols = ACTION_ORDER.filter((a) => present.has(a));
                const catIds = Array.from(screens.values()).flat().map((i) => i.permissionId);
                const catAllOn = catIds.every((id) => selectedPerms.has(id));

                return (
                  <div key={category} className="rm-cat">
                    <div className="rm-cat-bar">
                      <span className="rm-cat-name">{category}</span>
                      <button className="rm-linkbtn sm" onClick={() => setMany(catIds, !catAllOn)}>
                        {catAllOn ? "temizle" : "tümü"}
                      </button>
                    </div>
                    <div className="rm-table-wrap">
                      <table className="rm-table">
                        <thead>
                          <tr>
                            <th className="rm-th-screen">Ekran / Sekme</th>
                            {cols.map((a) => {
                              const colIds = Array.from(screens.values())
                                .flat()
                                .filter((i) => i.action === a)
                                .map((i) => i.permissionId);
                              const colOn = colIds.length > 0 && colIds.every((id) => selectedPerms.has(id));
                              return (
                                <th key={a} className="rm-th-act">
                                  <button className="rm-col-toggle" onClick={() => setMany(colIds, !colOn)}>
                                    {ACTION_LABELS[a] || a}
                                  </button>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from(screens.entries()).map(([screenCode, items]) => {
                            const screenName = items[0].screenName;
                            const hasTabs = items.some((i) => i.tabCode);
                            const rows: { label: string; code: string; sub: boolean; items: CatalogItem[] }[] = [];
                            if (hasTabs) {
                              rows.push({ label: screenName, code: screenCode, sub: false, items: [] });
                              const tabs = new Map<string, CatalogItem[]>();
                              for (const it of items) {
                                const k = it.tabCode || "_";
                                if (!tabs.has(k)) tabs.set(k, []);
                                tabs.get(k)!.push(it);
                              }
                              tabs.forEach((tabItems, k) =>
                                rows.push({
                                  label: tabItems[0].tabName || k,
                                  code: `${screenCode}:${k}`,
                                  sub: true,
                                  items: tabItems,
                                }),
                              );
                            } else {
                              rows.push({ label: screenName, code: screenCode, sub: false, items });
                            }

                            return rows.map((row, ri) => {
                              const rowIds = row.items.map((i) => i.permissionId);
                              // Sekmeli ekranın grup başlık satırı (kendi işlemi yok)
                              if (row.items.length === 0) {
                                return (
                                  <tr key={screenCode + ri} className="rm-tr-screen">
                                    <td className="rm-td-screen">
                                      <span className="rm-row-label">{row.label}</span>
                                      {showCodes && <code className="rm-row-code">{row.code}</code>}
                                    </td>
                                    <td className="rm-td-groupfill" colSpan={cols.length} />
                                  </tr>
                                );
                              }
                              const rowAllOn = rowIds.length > 0 && rowIds.every((id) => selectedPerms.has(id));
                              return (
                                <tr key={screenCode + ri} className={row.sub ? "rm-tr-sub" : ""}>
                                  <td className="rm-td-screen">
                                    <span className="rm-row-label">{row.label}</span>
                                    {showCodes && <code className="rm-row-code">{row.code}</code>}
                                    <button className="rm-row-toggle" onClick={() => setMany(rowIds, !rowAllOn)} title="Satırın tümünü seç/temizle">
                                      {rowAllOn ? "Temizle" : "Tümü"}
                                    </button>
                                  </td>
                                  {cols.map((a) => {
                                    const it = row.items.find((i) => i.action === a);
                                    if (!it) return <td key={a} className="rm-td-na"><span>—</span></td>;
                                    const on = selectedPerms.has(it.permissionId);
                                    return (
                                      <td key={a} className="rm-td-act">
                                        <label className={`rm-cell ${on ? "on" : ""}`} title={it.code}>
                                          <input type="checkbox" checked={on} onChange={() => toggle(it.permissionId)} />
                                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                        </label>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            });
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="rm-panel"><div className="rm-empty"><p>Yukarıdan bir rol seçin.</p></div></div>
      )}

      {/* Rol modal */}
      {roleModal.open && (
        <div className="rm-modal-overlay" onMouseDown={() => setRoleModal({ ...roleModal, open: false })}>
          <div className="rm-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="rm-modal-head">
              <h2>{roleModal.id == null ? "Yeni Rol" : "Rolü Düzenle"}</h2>
              <button className="rm-modal-close" onClick={() => setRoleModal({ ...roleModal, open: false })}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="rm-modal-body">
              <div className="rm-field">
                <label>Rol Adı (claim) {roleModal.isSystem && <span className="rm-hint">— yerleşik, değiştirilemez</span>}</label>
                <input
                  type="text"
                  value={roleModal.name}
                  disabled={roleModal.isSystem}
                  onChange={(e) => setRoleModal({ ...roleModal, name: e.target.value })}
                  placeholder="Örn: Depo Sorumlusu"
                />
                <small>Bu ad aynı zamanda rolün yetki (claim) adıdır.</small>
              </div>
              <div className="rm-field">
                <label>Açıklama</label>
                <textarea rows={2} value={roleModal.description} onChange={(e) => setRoleModal({ ...roleModal, description: e.target.value })} />
              </div>
            </div>
            <div className="rm-modal-foot">
              <button className="btn btn-secondary" onClick={() => setRoleModal({ ...roleModal, open: false })}>İptal</button>
              <button className="btn btn-primary" onClick={saveRole} disabled={saving}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert({ ...alert, isOpen: false })} />
      <ConfirmModal
        isOpen={confirm.isOpen}
        title="Rolü Sil"
        message={confirm.message}
        type="danger"
        confirmText="Evet, Sil"
        cancelText="İptal"
        onConfirm={confirm.onConfirm}
        onCancel={() => setConfirm({ isOpen: false, message: "", onConfirm: () => {} })}
      />
    </div>
  );
};

export default RoleManagement;
